import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { formatDateOnly } from "helpers/dateOnly";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { getQuotes } from "api/Quotes";
import { getProjects } from "api/Projects";
import { trackActivity } from "api/ActivityTracker";
import TableData from "components/ui/TableData";
import { createColumnHelper } from "@tanstack/react-table";
import { Badge, Box, Flex, Image } from "theme-ui";
import { useNavigate, useSearchParams } from "react-router-dom";
import Span from "components/typography/Span";
import SelectInput from "components/forms/SelectInput";
import UserContext from "context/User";
import moment from "moment-timezone";

/**
 * Filtered by status NAME, not id. The nine statuses are global rows
 * (companyId NULL) and the name is what anyone would type into a link:
 * `/projects?status=complete,verified` is "finished and not invoiced",
 * which is the question this list most needs to answer - 529 of ~700
 * projects are Closed, and without a filter the live ones are buried.
 *
 * Pipeline order, not alphabetical, so the options read as the job's
 * progress.
 */
const projectStatusOptions = [
  "New",
  "Pending",
  "Site Ready",
  "Scheduled",
  "Complete",
  "Verified",
  "Invoice Sent",
  "Closed",
  "Cancelled",
].map((name) => ({ value: name, label: name }));

const findStatus = (token: string) =>
  projectStatusOptions.find((o) => o.value.toLowerCase() === token.trim().toLowerCase());

const Projects = () => {
  const columnHelper = createColumnHelper();
  const navigate = useNavigate();
  const { user } = useContext<any>(UserContext);

  const [refreshData, setRefreshData] = useState<boolean>(false);
  const hasTrackedView = useRef(false);
  const [currentQuery, setCurrentQuery] = useState<any>(null);
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");

  // `?status=complete,verified` pre-applies the filter, so the dashboard
  // and anyone's bookmark can land on a working set rather than all 700.
  const preFilters = useMemo(() => {
    if (!statusParam) return undefined;
    const names = statusParam
      .split(",")
      .map((t) => findStatus(t)?.value)
      .filter(Boolean);
    return names.length ? { "status.name": `${names.join(",")}:in` } : undefined;
  }, [statusParam]);

  const handleFilterUrlSync = (filters: Record<string, string>) => {
    const raw = filters?.["status.name"];
    const names = raw
      ? `${raw}`.split(":")[0].split(",").map((n) => n.trim().toLowerCase()).filter(Boolean)
      : [];
    const query = names.length ? `?status=${names.join(",")}` : "";
    if (window.location.search !== query) {
      window.history.replaceState(null, "", `${window.location.pathname}${query}`);
    }
  };

  const tableFilters = [
    {
      key: "status.name",
      component: SelectInput,
      condition: "in",
      props: {
        name: "status",
        label: "Status",
        options: projectStatusOptions,
        clearable: true,
        searchable: false,
        multiple: true,
        $customStyles: { minWidth: "220px" },
      },
      convertValue: (value: any) => {
        if (Array.isArray(value)) {
          if (value.every((v: any) => typeof v === "object" && v !== null && "value" in v)) return value;
          return value.map((v: any) => findStatus(`${v}`)).filter(Boolean);
        }
        if (!value) return [];
        return `${value}`.split(":")[0].split(",").map((v: string) => findStatus(v)).filter(Boolean);
      },
      updateValue: (val: any) => {
        if (!val) return null;
        const values = Array.isArray(val) ? val : [val];
        const names = values
          .map((v: any) => (typeof v === "object" && v !== null && "value" in v ? v.value : `${v}`))
          .map((n: string) => findStatus(n)?.value)
          .filter(Boolean);
        return names.length ? names.join(",") : null;
      },
    },
  ];

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Projects";
    
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    
    void trackActivity({
      module: "projects",
      action: "view_projects_list",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: null,
      entityId: null,
      metadata: {
        page: currentQuery?.pageNum || 1,
        limit: currentQuery?.recordsPer || 15,
        filters: currentQuery?.filters || {},
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [user, currentQuery]);

  const columns = [
    columnHelper.accessor("sequence", {
      id: "sequence",
      cell: (info: any) => (
        <Box sx={{ fontWeight: 600, cursor: "pointer", color: "#000" }} onClick={() => navigate(`/projects/${info.row.original.id}`)}>
          {`GMP-${String(info.getValue()).padStart(5, "0")}`}
        </Box>
      ),
      header: () => <span>Project #</span>,
      meta: {
        style: { width: "150px", minWidth: "150px" },
      },
    }),
    columnHelper.accessor((row: any) => row.projectName, {
      id: "name",
      cell: (info: any) => (
        <Flex sx={{ gap: "1rem", alignItems: "center" }}>
          <Box>
            <Span color="#000" fontWeight="500">
              {info.getValue()}
            </Span>
            <Span color="var(--theme-ui-colors-base_500)" display="block">
              {info.row.original?.location?.address?.line1}
              {info.row.original?.location?.address?.line2 && ` ${info.row.original.location.address?.line2},`}
              {", "}
              {info.row.original.location?.address?.city}, {info.row.original.location?.address?.state} {info.row.original.location?.address?.postalCode === 'null' ? '' : info.row.original.location.address?.postalCode}
            </Span>
          </Box>
        </Flex>
      ),
      header: () => <span>Project Info</span>,
      enableSorting: false,
    }),
    ...(user.role !== null
      ? [
          columnHelper.accessor((row: any) => row?.client?.name, {
            id: "clientName",
            cell: (info: any) => info.getValue(),
            header: () => <span>Company</span>,
          }),
        ]
      : []),
       columnHelper.accessor((row: any) => row?.primaryClient?.name, {
      id: "primaryClient",
      cell: (info: any) => info.getValue(),
      header: () => <span>{user.role !== null ? 'Primary Client' : 'Primary Contact'}</span>,
    }),
    columnHelper.accessor((row: any) => row?.status, {
      id: "status",
      cell: (info: any) => {
        const status = info.getValue();
        return (
          <Badge as="span" variant={status.color}>
            {status.name}
          </Badge>
        );
      },
      header: () => <span>Status</span>,
    }),
    ...(user.role !== null
      ? [
          columnHelper.accessor(
            (row: any) => {
              const lineitems = row.originQuote?.lineitems || [];
              const providers = lineitems.map((item: any) => item?.depot?.provider?.name).filter((name: string | undefined): name is string => !!name);
              const uniqueProviders = Array.from(new Set(providers));
              return uniqueProviders.join(", ");
            },
            {
              id: "providers",
              header: () => <span>Providers</span>,
              cell: (info: any) => info.getValue(),
              enableSorting: false,
            }
          ),
        ]
      : []),
    columnHelper.accessor(
      (row: any) => {
        const productItem = row.originQuote?.lineitems?.find((item: any) => item.type === "product");
        if (!productItem) return "-";
        return `${productItem.quantity} ${productItem.unit}`;
      },
      {
        id: "bulkQuantity",
        header: () => <span>Quantity</span>,
        cell: (info: any) => info.getValue(),
        enableSorting: false,
      }
    ),
    columnHelper.accessor((row: any) => row?.serviceDate, {
      id: "serviceDate",
      // Calendar date: shown as picked (helpers/dateOnly), not shifted a day by the browser zone.
      cell: (info: any) => formatDateOnly(info.getValue()),
      header: () => <span>Estimated Service Date</span>,
    }),
    columnHelper.accessor((row: any) => row?.scheduleDate, {
      id: "scheduleDate",
      cell: (info: any) => formatDateOnly(info.getValue()),
      header: () => <span>Scheduled Date</span>,
    }),
    columnHelper.accessor((row: any) => row?.createdAt, {
      id: "dateAdded",
      cell: (info: any) => (info.getValue() ? moment(info.getValue()).format("MMM D, YYYY") : ""),
      header: () => <span>Date Added</span>,
    }),
  ];

  return (
    <>
      <Header></Header>
      <Page title="Projects" subtitle={`Manage projects for your company.`}>
        <TableData
          api={getProjects}
          columns={columns}
          refresh={refreshData}
          title={"All Projects"}
          defaultSortBy="sequence"
          defaultSortDir="desc"
          nothingFoundText="No Projects found."
          filters={tableFilters}
          preFilters={preFilters}
          onFiltersChange={handleFilterUrlSync}
          returnQuery={setCurrentQuery}
          filtersStyles={{ display: "grid", gap: "16px", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
        />
      </Page>
    </>
  );
};

export default Projects;
